// @ts-nocheck
/**
 * Theme helpers for ApexCharts. Each builder reads CSS variables at call time
 * so charts respect the current light/dark palette. Re-call when the theme
 * changes (or pass the result fresh to ReactApexChart's `options` prop).
 */
export const fontStack = '"Inter Tight", system-ui, -apple-system, "Segoe UI", sans-serif';
export const monoStack = '"JetBrains Mono", "SF Mono", Menlo, monospace';

const cssVar = (name, fallback) => {
  if (typeof window === 'undefined') return fallback;
  const v = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
  return v || fallback;
};

const palette = () => [
  cssVar('--mov-accent', '#7ac142'),
  cssVar('--mov-accent-ink', '#3a4a2a'),
  cssVar('--mov-warn', '#c9912a'),
  '#43bccd',
  '#7913c6',
  '#f86624',
  cssVar('--mov-danger', '#ea3546'),
  cssVar('--mov-ink-mute', '#8a918c'),
];

export const movPalette = new Proxy([], {
  get(_t, k) { return palette()[k]; },
  ownKeys() { return palette().map((_, i) => String(i)); },
  has(_t, k) { return Number(k) < palette().length; },
  getOwnPropertyDescriptor() {
    return { enumerable: true, configurable: true };
  },
});

const isDarkTheme = () => (
  typeof document !== 'undefined' && document.documentElement.dataset.theme === 'dark'
);

/**
 * Base ApexCharts options matching the movdashboard design tokens. Spread
 * onto each chart's `options` prop. Pass overrides via the second arg.
 */
export const apexBase = (overrides = {}) => {
  const inkSoft = cssVar('--mov-ink-soft', '#5a625f');
  const inkMute = cssVar('--mov-ink-mute', '#8a918c');
  const line = cssVar('--mov-line', '#e6e3db');
  const paper = cssVar('--mov-paper', '#fff');
  const isDark = isDarkTheme();

  return {
    chart: {
      fontFamily: fontStack,
      toolbar: { show: false },
      background: 'transparent',
      animations: { enabled: true, speed: 250 },
      ...(overrides.chart || {}),
    },
    grid: {
      borderColor: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(20,24,26,0.06)',
      strokeDashArray: 0,
      xaxis: { lines: { show: false } },
      yaxis: { lines: { show: true } },
      padding: { top: 0, right: 8, bottom: 0, left: 8 },
      ...(overrides.grid || {}),
    },
    xaxis: {
      labels: { style: { colors: inkMute, fontSize: '11px', fontFamily: fontStack } },
      axisBorder: { show: false },
      axisTicks: { show: false },
      ...(overrides.xaxis || {}),
    },
    yaxis: {
      labels: { style: { colors: inkMute, fontSize: '11px', fontFamily: fontStack } },
      ...(overrides.yaxis || {}),
    },
    legend: {
      position: 'top',
      horizontalAlign: 'left',
      fontFamily: fontStack,
      fontSize: '12px',
      labels: { colors: inkSoft },
      markers: { width: 10, height: 10, radius: 2 },
      itemMargin: { horizontal: 12, vertical: 4 },
      ...(overrides.legend || {}),
    },
    tooltip: {
      theme: isDark ? 'dark' : 'light',
      style: { fontFamily: fontStack, fontSize: '12px' },
      ...(overrides.tooltip || {}),
    },
    dataLabels: { enabled: false, ...(overrides.dataLabels || {}) },
    stroke: { width: 0, ...(overrides.stroke || {}) },
    states: {
      hover: { filter: { type: 'darken', value: 0.95 } },
      active: { filter: { type: 'none' } },
    },
    theme: { mode: isDark ? 'dark' : 'light' },
    colors: overrides.colors || palette(),
    ...Object.fromEntries(
      Object.entries(overrides).filter(([k]) => !['chart', 'grid', 'xaxis', 'yaxis', 'legend', 'tooltip', 'dataLabels', 'stroke', 'colors'].includes(k)),
    ),
  };
};
